import { ThemeEvents } from '@theme/events';

/**
 * Progress bar for product card galleries, rendered outside the gallery container
 * and positioned after all other card content via CSS order:999.
 *
 * Connects to the slideshow via the 'slidechange' CustomEvent dispatched by
 * slideshow.js. Slide count is read directly from the DOM (non-hidden
 * slideshow-slide elements), so it works for both metafield-based and
 * standard-image products.
 */
class CardGalleryProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #card = null;

  connectedCallback() {
    this.#card = this.closest('product-card');
    if (!this.#card) return;

    this.#init();
    this.#card.addEventListener('slidechange', this.#handleSlideChange);
    this.#card.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.#card?.removeEventListener('slidechange', this.#handleSlideChange);
    this.#card?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#card = null;
  }

  #init() {
    const slideshow = this.#card?.querySelector('slideshow-component[ref="slideshow"]');
    if (!slideshow) return;

    if (slideshow.getAttribute('disabled') === 'true') {
      this.hidden = true;
      return;
    }

    // Count non-hidden slides directly from the DOM — works for both
    // metafield-image products and standard products with generic images.
    const slideCount = slideshow.querySelectorAll('slideshow-slide:not([hidden])').length;

    if (slideCount <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.style.setProperty('--slide-current', '0');
    this.style.setProperty('--slide-count', String(slideCount));

    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[role="progressbar"]'));
    if (bar) {
      bar.setAttribute('aria-valuemax', String(slideCount));
      bar.setAttribute('aria-valuenow', '1');
      bar.style.setProperty('--slide-count', String(slideCount));
    }
  }

  /** @param {Event} event */
  #handleSlideChange = (event) => {
    if (this.hidden) return;
    const { current } = /** @type {{ current: number }} */ (/** @type {CustomEvent} */ (event).detail);
    this.style.setProperty('--slide-current', String(current));
    /** @type {HTMLElement | null} */ (this.querySelector('[role="progressbar"]'))?.setAttribute('aria-valuenow', String(current + 1));
  };

  #handleVariantUpdate = () => {
    requestAnimationFrame(() => this.#init());
  };
}

if (!customElements.get('card-gallery-progress')) {
  customElements.define('card-gallery-progress', CardGalleryProgress);
}
